import { BECategory } from 'constants/categories';
import type { Category, GetCategoryResponse } from 'lib/bff/types/category.types';
import type { EnhancedCard, EnhancedMarket, MarketCard } from 'lib/bff/types/feed.types';
import type { GetPublicHomeResponse, GetUserHomeResponse, Highlight } from 'lib/bff/types/home.types';
import type { EventVo } from 'models/event';
import {
    type MarketVo,
    marketLastPriceCents,
    marketPrevPeriodPriceCents,
    marketPreviousDayPriceCents,
    marketYesAskCents,
    marketYesBidCents,
    complementCents,
} from 'models/market';
import type { MilestoneVo, StructuredTargetVo } from 'models/milestones';
import { resolveEffectiveFeeParams } from 'utils/fees';
import { getMarketCardImageTarget, getMarketRowTargetImageUrl } from 'utils/marketRowImage';
import { getStructuredTargetImageUrl } from 'utils/teams';

type EnrichCardOptions = {
    expandSportsEventMarkets?: boolean;
};

const getStructuredTargetKey = (target: StructuredTargetVo): string | undefined => {
    const legacyTarget = target as StructuredTargetVo & { structured_target_id?: string };
    return target.id ?? legacyTarget.structured_target_id;
};

const mapMarketStatus = (status: MarketVo['status']): EnhancedMarket['status'] | undefined => {
    if (status === 'active') return 'open';
    if (status === 'initialized' || status === 'inactive' || status === 'invalid') return 'pending';
    if (status === 'closed') return 'closed';
    if (status === 'settled') return 'settled';
    if (status === 'determined' || status === 'finalized' || status === 'amended' || status === 'disputed') {
        return 'determined';
    }
    return undefined;
};

const getStructuredTargetIdFromMarket = (
    market: MarketVo,
    structuredTargets: Record<string, StructuredTargetVo>,
): string | undefined => {
    const marketWithStructuredTarget = market as MarketVo & { structured_target_id?: unknown };
    if (typeof marketWithStructuredTarget.structured_target_id === 'string') {
        return marketWithStructuredTarget.structured_target_id;
    }

    return Object.values(market.custom_strike ?? {}).find(
        (value): value is string => typeof value === 'string' && !!structuredTargets[value],
    );
};

const convertEventMarketToEnhancedMarket = (
    market: MarketVo,
    structuredTargets: Record<string, StructuredTargetVo>,
): EnhancedMarket => {
    const structuredTargetId = getStructuredTargetIdFromMarket(market, structuredTargets);
    const structuredTarget = structuredTargetId ? structuredTargets[structuredTargetId] : undefined;
    const price = Number.isFinite(marketLastPriceCents(market))
        ? marketLastPriceCents(market)
        : Math.round((marketYesBidCents(market) + marketYesAskCents(market)) / 2);
    const previousPrice = marketPreviousDayPriceCents(market) ?? marketPrevPeriodPriceCents(market) ?? price;
    const productDetails = structuredTarget?.product_details;
    const canonicalImage = getStructuredTargetImageUrl(structuredTarget);

    return {
        ticker: market.ticker_name,
        title: market.title || market.yes_sub_title || market.ticker_name,
        yes_bid: marketYesBidCents(market),
        yes_ask: marketYesAskCents(market),
        no_bid: Math.max(0, complementCents(marketYesAskCents(market))),
        no_ask: Math.max(0, complementCents(marketYesBidCents(market))),
        price,
        previous_price: previousPrice,
        open_date: market.open_date,
        close_date: market.close_date,
        image_url: canonicalImage,
        image_url_dark_mode: canonicalImage,
        image_url_light_mode: canonicalImage,
        structured_target_id: structuredTargetId,
        custom_strike: market.custom_strike,
        structured_target: structuredTarget,
        volume: market.volume,
        dollar_volume: market.dollar_volume,
        open_interest: market.open_interest,
        dollar_open_interest: market.dollar_open_interest,
        status: mapMarketStatus(market.status),
        result: market.result,
        yes_subtitle: market.yes_sub_title,
        colors: market.colors ?? structuredTarget?.colors,
        color_palette: productDetails?.color_palette ?? productDetails?.['Color Palette'],
    };
};

const getMarketsToEnrich = ({
    card,
    event,
    structuredTargets,
    options,
}: {
    card: EnhancedCard;
    event?: EventVo;
    structuredTargets: Record<string, StructuredTargetVo>;
    options?: EnrichCardOptions;
}): Array<MarketCard | EnhancedMarket> | undefined => {
    const cardMarkets = card.markets;
    const shouldExpandSportsMarkets =
        options?.expandSportsEventMarkets &&
        (card.category === BECategory.SPORTS || event?.category === BECategory.SPORTS) &&
        event?.markets &&
        event.markets.length > (cardMarkets?.length ?? 0);

    if (!shouldExpandSportsMarkets) {
        return cardMarkets;
    }

    const relationTargets = {
        ...structuredTargets,
        ...(card.structured_targets ?? {}),
    };

    return event.markets.map((market) => convertEventMarketToEnhancedMarket(market, relationTargets));
};

/**
 * Enriches a single market with its structured target data and derived color_palette.
 */
function enrichMarket(
    market: MarketCard | EnhancedMarket,
    structuredTargets: Record<string, StructuredTargetVo>,
): EnhancedMarket {
    const hydratedTarget = 'structured_target' in market ? market.structured_target : undefined;
    const target = market.structured_target_id
        ? (structuredTargets[market.structured_target_id] ?? hydratedTarget)
        : hydratedTarget;
    const politicalPartyTargetId = market.associated_structured_targets?.political_party;
    const politicalPartyTarget = politicalPartyTargetId ? structuredTargets[politicalPartyTargetId] : undefined;
    const imageTarget = getMarketCardImageTarget({ ...market, structured_target: target }, structuredTargets);

    if (!target && !politicalPartyTarget && !imageTarget) {
        return market;
    }

    const partyProductDetails = politicalPartyTarget?.product_details;
    const targetProductDetails = target?.product_details;
    const imageUrl = getMarketRowTargetImageUrl(imageTarget);
    return {
        ...market,
        // Artwork follows the candidate; the associated party still supplies the palette.
        ...(imageUrl
            ? {
                  image_url: imageUrl,
                  image_url_light_mode: imageUrl,
                  image_url_dark_mode: imageUrl,
              }
            : {}),
        structured_target: target,
        colors: politicalPartyTarget?.colors ?? market.colors ?? target?.colors,
        color_palette:
            partyProductDetails?.color_palette ??
            partyProductDetails?.['Color Palette'] ??
            targetProductDetails?.color_palette ??
            targetProductDetails?.['Color Palette'] ??
            (market as EnhancedMarket).color_palette ??
            undefined,
    };
}

/**
 * Enriches a card with milestone and structured target data.
 *
 * This utility function:
 * 1. Adds the milestone object to the card if it has a milestone_id
 * 2. Adds structured_target objects to each market in the card that has a structured_target_id
 *
 * @param card - The card to enrich
 * @param milestones - Record of milestone_id to MilestoneVo
 * @param structuredTargets - Record of structured_target_id to StructuredTargetVo
 * @returns The enriched card with milestone and structured target data populated
 *
 * @example
 * ```ts
 * const enrichedCard = enrichCardWithRelations(
 *   card,
 *   { 'milestone-1': milestoneData },
 *   { 'target-1': targetData }
 * );
 * ```
 */
export function enrichCardWithRelations(
    card: EnhancedCard,
    milestones: Record<string, MilestoneVo>,
    structuredTargets: Record<string, StructuredTargetVo>,
    events?: Record<string, EventVo>,
    options?: EnrichCardOptions,
): EnhancedCard {
    const result: EnhancedCard = {
        ...card,
        series_fee_type: card.series_fee_type ?? card.fee_type,
        series_fee_multiplier: card.series_fee_multiplier ?? card.fee_multiplier,
    };
    const event = card.event_ticker ? events?.[card.event_ticker] : undefined;

    // Add milestone if the card has a milestone_id
    if (card.milestone_id && milestones[card.milestone_id]) {
        result.milestone = milestones[card.milestone_id];
        // Set start_date from milestone for MarketTile countdown display
        result.start_date = result.milestone.start_date ?? card.start_date;
    }

    if (event) {
        // Event-page display overrides (for example `trading_page_title`) live in
        // `product_metadata_derived`, while highlight cards only expose a single
        // `product_metadata` bag. Merge the hydrated event metadata into that bag
        // so card surfaces can use the same labels as the event page.
        result.product_metadata = {
            ...(event.product_metadata ?? {}),
            ...(card.product_metadata ?? {}),
            ...(event.product_metadata_derived ?? {}),
        };
    }

    // The card's own fee fields are the series defaults applied by the BFF.
    // Resolve them even when the card has no hydrated event so web-only series
    // defaults still reach every card surface; event overrides win when present.
    const effectiveFees = resolveEffectiveFeeParams({
        series: { ticker: card.series_ticker, fee_type: card.fee_type, fee_multiplier: card.fee_multiplier },
        overrides: event,
    });
    result.fee_type = effectiveFees.fee_type;
    result.fee_multiplier = effectiveFees.fee_multiplier;

    // Enrich markets and collect every structured target the card references
    // — both `structured_target_id` and any `associated_structured_targets`
    // value, plus any id surfaced through `milestone.details` (e.g.
    // `home_team_id`/`away_team_id` on totals cards where the markets
    // themselves don't carry a `structured_target_id`) — into a per-card map
    // so consumers can resolve by preferred type without re-walking the
    // response-level map.
    const markets = getMarketsToEnrich({ card, event, structuredTargets, options });
    if (markets) {
        const targets: Record<string, StructuredTargetVo> = {};
        // Follow a player target's `details.team_id` into the response-level
        // map so consumers (e.g. player-prop cards) can resolve team color
        // palettes without the BFF having to also reference the team via
        // `associated_structured_targets`.
        const addTarget = (target: StructuredTargetVo | undefined) => {
            if (!target) return;
            const targetId = getStructuredTargetKey(target);
            if (!targetId || targets[targetId]) return;
            targets[targetId] = target;
            const teamId = target.details?.team_id;
            if (teamId && !targets[teamId]) {
                const team = structuredTargets[teamId];
                if (team) targets[teamId] = team;
            }
        };
        result.markets = markets.map((market) => {
            const enriched = enrichMarket(market, structuredTargets);
            addTarget(enriched.structured_target);
            if (market.associated_structured_targets) {
                for (const id of Object.values(market.associated_structured_targets)) {
                    if (id) addTarget(structuredTargets[id]);
                }
            }
            return enriched;
        });
        // Walk every string value in `milestone.details` and pick up any that
        // happens to be a key in the response-level `structured_targets` map.
        // This is intentionally schema-agnostic: milestone shapes vary across
        // sports/event types (`home_team_id`, `away_competitor_id`,
        // `first_fighter_id`, ...) and only the values that resolve to a real
        // target get added.
        const milestoneDetails = result.milestone?.details;
        if (milestoneDetails) {
            for (const value of Object.values(milestoneDetails)) {
                if (typeof value === 'string') {
                    addTarget(structuredTargets[value]);
                }
            }
        }
        result.structured_targets = targets;
    }

    return result;
}

type CardProductMetadata = NonNullable<EnhancedCard['product_metadata']>;

/**
 * Overlays a thinned inline `product_metadata` onto its hydrated counterpart so
 * keys the inline copy omits (e.g. `custom_image_url`) survive, while keys it
 * does define still win. Keys explicitly set to `undefined` on the inline copy
 * do not clobber the hydrated value.
 */
const mergeProductMetadata = (
    hydrated: CardProductMetadata | undefined,
    inline: CardProductMetadata | undefined,
): CardProductMetadata | undefined => {
    if (!hydrated) return inline;
    if (!inline) return hydrated;

    const merged: CardProductMetadata = { ...hydrated };
    for (const [key, value] of Object.entries(inline)) {
        if (value !== undefined) {
            merged[key] = value;
        }
    }
    return merged;
};

/**
 * Enriches a home endpoint response with milestone and structured target data.
 *
 * This utility function processes the home response data to:
 * 1. Enrich highlight cards with milestone and structured target data
 * 2. Enrich category search_cards and recommendation_cards with milestone and structured target data
 *
 * @param data - The home response data (GetPublicHomeResponse or GetUserHomeResponse)
 * @returns The enriched response data with all cards populated with related data
 *
 * @example
 * ```ts
 * this.bffApi.get('/home').then((response) => {
 *   const enrichedData = enrichHomeResponseWithRelations(response.data);
 *   onSuc(enrichedData);
 * });
 * ```
 */
export function enrichHomeResponseWithRelations(
    data: GetPublicHomeResponse | GetUserHomeResponse,
): GetPublicHomeResponse | GetUserHomeResponse {
    const { hydrated_data, home } = data;
    const { milestones = {}, structured_targets = {}, cards: hydratedCards = {} } = hydrated_data;
    const { highlights = [], categories = [] } = home;

    const events = hydrated_data.events ?? {};

    const enrichedHighlights = highlights.map((highlight: Highlight) => {
        const inlineCard = highlight.card;
        // Perps highlights carry a margin card with no event relations to join.
        if (!inlineCard || highlight.type === 'perps') {
            return highlight;
        }
        // The BFF thins each highlight's inline card — `product_metadata` and
        // `tags` are only present on the full copy in `hydrated_data.cards`.
        // Backfill them so highlight surfaces (e.g. the home slideshow) resolve
        // the same custom image override and overline tags as feed/category
        // surfaces, which receive full cards.
        // `product_metadata` is merged key-by-key (hydrated first, inline on top)
        // because the inline copy is sometimes partial — e.g. it carries
        // `competition`/`competition_scope` but not `custom_image_url` — and a
        // whole-object fallback would treat it as complete and drop the override.
        const hydratedCard = hydratedCards[inlineCard.event_ticker];
        const card: EnhancedCard = hydratedCard
            ? {
                  ...inlineCard,
                  product_metadata: mergeProductMetadata(hydratedCard.product_metadata, inlineCard.product_metadata),
                  tags: inlineCard.tags?.length ? inlineCard.tags : (hydratedCard.tags ?? inlineCard.tags),
              }
            : inlineCard;
        return {
            ...highlight,
            card: enrichCardWithRelations(card, milestones, structured_targets, events, {
                expandSportsEventMarkets: true,
            }),
        };
    });

    const enrichedCategories = categories.map((category: Category) => ({
        ...category,
        search_cards: category.search_cards?.map((card) =>
            enrichCardWithRelations(card, milestones, structured_targets),
        ),
        recommendation_cards: category.recommendation_cards?.map((card) =>
            enrichCardWithRelations(card, milestones, structured_targets),
        ),
    }));

    return {
        ...data,
        home: {
            ...home,
            ...(home.highlights === undefined ? {} : { highlights: enrichedHighlights }),
            ...(home.categories === undefined ? {} : { categories: enrichedCategories }),
        },
    };
}

/**
 * Enriches a category endpoint response with milestone and structured target data.
 *
 * This utility function processes the category response data to enrich search_cards
 * and recommendation_cards with milestone and structured target data.
 *
 * @param data - The category response data (GetCategoryResponse)
 * @returns The enriched response data with all cards populated with related data
 *
 * @example
 * ```ts
 * this.bffApi.get('/category/politics').then((response) => {
 *   const enrichedData = enrichCategoryResponseWithRelations(response.data);
 *   onSuc(enrichedData);
 * });
 * ```
 */
export function enrichCategoryResponseWithRelations(data: GetCategoryResponse): GetCategoryResponse {
    const { hydrated_data, category } = data;
    const { milestones = {}, structured_targets = {} } = hydrated_data;

    const enrichedCategory: Category = {
        ...category,
        search_cards: category.search_cards?.map((card) =>
            enrichCardWithRelations(card, milestones, structured_targets),
        ),
        recommendation_cards: category.recommendation_cards?.map((card) =>
            enrichCardWithRelations(card, milestones, structured_targets),
        ),
    };

    return {
        ...data,
        category: enrichedCategory,
    };
}
